/*
  <adaptive-gallery-height>
  Wraps the product gallery <slider-component>. On mobile, sets the slider's height
  to the height of the slide currently in view, so mixed aspect ratios don't leave
  blank space under shorter images.

  Relies on the theme's <slider-component>, which fires "slideChanged" on swipe/arrow/dot.
  Does not modify any base theme JS.
*/
if (!customElements.get('adaptive-gallery-height')) {
  customElements.define(
    'adaptive-gallery-height',
    class AdaptiveGalleryHeight extends HTMLElement {
      connectedCallback() {
        this.viewer = this.querySelector('slider-component');
        this.slider = this.querySelector('.product__media-list');
        if (!this.viewer || !this.slider) return;

        this.desktopQuery = window.matchMedia('(min-width: 750px)');
        this.scrollTimeout = null;

        this.onUpdate = this.updateHeight.bind(this);
        this.onScroll = this.onScroll.bind(this);

        this.viewer.addEventListener('slideChanged', this.onUpdate);
        this.slider.addEventListener('scroll', this.onScroll, { passive: true });
        this.desktopQuery.addEventListener('change', this.onUpdate);

        // Re-measure when a slide changes size (lazy image loads, rotation, variant filtering)
        this.resizeObserver = new ResizeObserver(this.onUpdate);
        this.slides().forEach((slide) => this.resizeObserver.observe(slide));

        this.updateHeight();
      }

      disconnectedCallback() {
        if (!this.viewer || !this.slider) return;
        this.viewer.removeEventListener('slideChanged', this.onUpdate);
        this.slider.removeEventListener('scroll', this.onScroll);
        this.desktopQuery.removeEventListener('change', this.onUpdate);
        this.resizeObserver.disconnect();
        window.clearTimeout(this.scrollTimeout);
      }

      slides() {
        return Array.from(this.slider.querySelectorAll('.product__media-item'));
      }

      // Fallback for cases where slideChanged doesn't fire (e.g. variant image jumps)
      onScroll() {
        window.clearTimeout(this.scrollTimeout);
        this.scrollTimeout = window.setTimeout(this.onUpdate, 100);
      }

      // The visible slide is the one whose left edge is closest to the current scroll position
      getActiveSlide() {
        const visibleSlides = this.slides().filter((slide) => slide.offsetParent !== null);
        if (!visibleSlides.length) return null;

        const scrollLeft = this.slider.scrollLeft;
        return visibleSlides.reduce((closest, slide) =>
          Math.abs(slide.offsetLeft - scrollLeft) < Math.abs(closest.offsetLeft - scrollLeft) ? slide : closest
        );
      }

      updateHeight() {
        if (this.desktopQuery.matches) {
          this.slider.style.removeProperty('height');
          return;
        }

        const activeSlide = this.getActiveSlide();
        if (!activeSlide) return;

        const height = activeSlide.offsetHeight;
        if (height > 0) this.slider.style.height = `${height}px`;
      }
    }
  );
}
